import {
  findActiveHoliday,
  findNextOpenTime,
  findNextScheduledOpening,
  findOpenUntil,
  HolidayWindow,
  isWithinBusinessHours,
} from '@provision/sidekick-utils';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { DATE_TIME_FORMAT } from 'src/constants/date';
import { usePartnerBusinessHours } from 'src/graphql/entities/partner-business-hours';
import { usePartnerHolidays } from 'src/graphql/entities/partner-holiday/hooks/use-partner-holiday';
import { ConditionInput } from 'src/graphql/types';
import { useCurrentDateTimer } from 'src/utils/hooks/use-current-date-timer';
import {
  toPartnerHolidayWindows,
  toPartnerTimeWindows,
} from 'src/utils/partner-business-hours';
import { PartnerBusinessState, PartnerBusinessStatus } from './partner-business-status.types';

// The status only needs closures that can still affect it: one in force now, or one covering a
// day within the evaluator's own lookahead window.
const HOLIDAY_LOOKAHEAD_DAYS = 15;
// Holiday columns are naked timestamps, so the bounds are padded by a day rather than matched
// exactly — any timezone skew between the filter and the partner's own clock stays inside it.
const HOLIDAY_LOOKBEHIND_DAYS = 1;
const HOLIDAY_FETCH_LIMIT = 100;

export function usePartnerBusinessStatus(
  partnerId?: number | null,
  timeZone?: string | null,
): PartnerBusinessStatus {
  const currentTime = useCurrentDateTimer();
  const skip = isNullish(partnerId);
  const timezone = timeZone ?? undefined;

  const { partnerBusinessHours, isLoaded: isBusinessHoursLoaded } = usePartnerBusinessHours(
    { partnerId: partnerId ?? undefined },
    { skip },
  );

  // Keyed on the date, not on the minute tick, so the query variables stay stable between ticks.
  const today = dayjs(currentTime).format('YYYY-MM-DD');
  const holidayFilters = useMemo(() => buildHolidayFilters(today), [today]);

  const {
    partnerHolidays,
    loading: isHolidaysLoading,
    error: holidaysError,
  } = usePartnerHolidays(
    { partnerId: partnerId ?? undefined, filters: holidayFilters, limit: HOLIDAY_FETCH_LIMIT },
    { skip },
  );

  const weeklyHours = useMemo(
    () => toPartnerTimeWindows(partnerBusinessHours),
    [partnerBusinessHours],
  );

  const holidays = useMemo(
    () => toPartnerHolidayWindows(partnerHolidays, timezone),
    [partnerHolidays, timezone],
  );

  return useMemo(() => {
    const context = {
      currentTime,
      timezone,
      businessHours: weeklyHours,
      holidays,
    };

    const activeHoliday = findActiveHoliday(currentTime, holidays);
    const isOpen = isWithinBusinessHours(context);
    const openUntil = isOpen ? findOpenUntil(context) : null;

    // `findOpenUntil` already stops at a closure that starts before the day's windows end, so
    // a closure beginning exactly then is the one about to interrupt business today.
    const closureLaterToday = openUntil ? findClosureStartingAt(openUntil, holidays) : null;
    const nextScheduledOpening = closureLaterToday
      ? null
      : findNextScheduledOpening(currentTime, context);
    const upcomingHoliday =
      closureLaterToday ??
      (nextScheduledOpening ? findActiveHoliday(nextScheduledOpening, holidays) : null);

    return {
      // A failed holiday query must not read as "no closures" — that would confidently
      // report Open in the middle of one. Hiding the line is the safer wrong answer.
      isLoaded: !skip && isBusinessHoursLoaded && !isHolidaysLoading && !holidaysError,
      state: resolveState(isOpen, activeHoliday),
      currentTime,
      activeHoliday,
      upcomingHoliday,
      upcomingHolidayAt: closureLaterToday ? openUntil : nextScheduledOpening,
      upcomingReopensAt: upcomingHoliday
        ? findNextOpenTime(new Date(upcomingHoliday.holidayEnd), context)
        : null,
      openUntil,
      opensAt: isOpen ? null : findNextOpenTime(currentTime, context),
      weeklyHours,
      timeZone: timezone,
    };
  }, [
    currentTime,
    holidays,
    holidaysError,
    isBusinessHoursLoaded,
    isHolidaysLoading,
    skip,
    timezone,
    weeklyHours,
  ]);
}

function buildHolidayFilters(today: string): ConditionInput[] {
  const startOfToday = dayjs(today).startOf('day');

  return [
    {
      attribute: 'holidayEnd',
      operator: '>',
      value: startOfToday.subtract(HOLIDAY_LOOKBEHIND_DAYS, 'day').format(DATE_TIME_FORMAT),
    },
    {
      attribute: 'holidayStart',
      operator: '<=',
      value: startOfToday.add(HOLIDAY_LOOKAHEAD_DAYS, 'day').format(DATE_TIME_FORMAT),
    },
  ];
}

function findClosureStartingAt(
  instant: Date,
  holidays: HolidayWindow[],
): HolidayWindow | null {
  const target = instant.getTime();

  return (
    holidays.find((holiday) => new Date(holiday.holidayStart).getTime() === target) ?? null
  );
}

function resolveState(
  isOpen: boolean,
  activeHoliday: HolidayWindow | null,
): PartnerBusinessState {
  if (isOpen) return PartnerBusinessState.Open;
  return activeHoliday ? PartnerBusinessState.Holiday : PartnerBusinessState.Closed;
}
